import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { ControleAdministrativoService } from '@/core/api/sicom-api/registro/medico/relatorios/controle-administrativo/controle-administrativo.service';
import { ReportControleAdministrativoExportResponse, ReportControleAdministrativoRequest } from '@/core/api/sicom-api/registro/medico/relatorios/controle-administrativo/controle-administrativo.model';
import { ControleAdministrativoJobService } from '@/core/services/controle-administrativo-job.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportControleAdministrativoRequest;
type Resp = ReportControleAdministrativoExportResponse;

interface ControleAdministrativoFormControls {
    mes: FormControl<number | null>;
    ano: FormControl<number | null>;
}

@Component({
    selector: 'app-controle-administrativo',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, SelectModule, InputNumber, SelectEstado, RelatorioContainer, CanDirective],
    templateUrl: './controle-administrativo.html',
    styleUrl: './controle-administrativo.scss'
})
/**
 * Relatório PDF assíncrono de Controle Administrativo (sgUf/mes/ano).
 * Herda `RelatorioFormBase`: toda a orquestração de export/polling/download/erro vem da base;
 * aqui só declaramos o formulário tipado, a seleção de UF e delegamos o HTTP ao service.
 */
export class ControleAdministrativo extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.medicos.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly controleAdministrativoService = inject(ControleAdministrativoService);
    readonly controleAdministrativoJobService = inject(ControleAdministrativoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.controleAdministrativoJobService.job$;

    form: FormGroup<ControleAdministrativoFormControls> = this.formBuilder.group<ControleAdministrativoFormControls>({
        mes: new FormControl<number | null>(null, Validators.required),
        ano: new FormControl<number | null>(new Date().getFullYear(), [Validators.required, Validators.min(2000)])
    });

    ufSelecionada = '';

    readonly opcoesMeses = [
        { label: 'Janeiro', value: 1 },
        { label: 'Fevereiro', value: 2 },
        { label: 'Março', value: 3 },
        { label: 'Abril', value: 4 },
        { label: 'Maio', value: 5 },
        { label: 'Junho', value: 6 },
        { label: 'Julho', value: 7 },
        { label: 'Agosto', value: 8 },
        { label: 'Setembro', value: 9 },
        { label: 'Outubro', value: 10 },
        { label: 'Novembro', value: 11 },
        { label: 'Dezembro', value: 12 }
    ];

    protected get jobFacade(): RelatorioJobFacade {
        return this.controleAdministrativoJobService;
    }

    /** Monta o request a partir do form + UF selecionada; retorna null se form inválido ou sem UF. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufTocada = true;

        if (this.form.invalid || !this.ufSelecionada) return null;

        const { mes, ano } = this.form.getRawValue();
        return { sgUf: this.ufSelecionada, mes: mes as number, ano: ano as number };
    }

    protected exportar(request: Req) {
        return this.controleAdministrativoService.exportarPdf(request);
    }

    alterarUf(uf: string | null): void {
        this.ufSelecionada = uf ?? '';
    }

    limparFormulario(): void {
        this.form.reset({ mes: null, ano: new Date().getFullYear() });
        this.ufSelecionada = '';
        this.ufTocada = false;
        this.limparJob();
    }
}
